import React from 'react';
import { FaTreeCity } from "react-icons/fa6";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { LayerPanelProps, GeocodeResult, LayerControlData } from '../../types';
import { api } from '../../lib/api';
import SearchBar from './SearchBar';
import LayerToggle from './LayerToggle';
import styles from './LayerPanel.module.css';

const LayerPanel: React.FC<LayerPanelProps> = ({
  layers,
  onLayerToggle,
  onVisibilityToggle,
  onFilterChange,
  onTransparencyChange,
  onLayerReorder,
  onAddressSelect
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const sortedLayers = [...layers].sort((a, b) => a.order - b.order);
  const [activeLayer, setActiveLayer] = React.useState<LayerControlData | null>(null);

  const handleDragStart = (event: DragStartEvent) => {
    const layer = sortedLayers.find(l => l.id === event.active.id);
    setActiveLayer(layer || null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = sortedLayers.findIndex((layer) => layer.id === active.id);
      const newIndex = sortedLayers.findIndex((layer) => layer.id === over?.id);

      const reorderedLayers = arrayMove(sortedLayers, oldIndex, newIndex);
      
      // Update order values to maintain consistency
      const updatedLayers = reorderedLayers.map((layer, index) => ({
        ...layer,
        order: index
      }));

      onLayerReorder(updatedLayers);
    }
    
    setActiveLayer(null);
  };

  const handleControlChange = (layerId: string, controlType: string, value: string | number) => {
    if (controlType === 'transparency') {
      onTransparencyChange(layerId, value as number);
    } else {
      onFilterChange(layerId, controlType, value as string);
    }
  };

  const handleSearch = async (query: string): Promise<GeocodeResult[]> => {
    // Use the backend proxy to avoid CORS issues
    try {
      return await api.geocodeAddress(query);
    } catch (error) {
      console.error('Geocoding error:', error);
      throw error; // Re-throw to let SearchBar handle the error display
    }
  };

  return (
    <div className={styles.layerPanel}>
      <div className={styles.layerPanelHeader}>
        <div className={styles.layerPanelLogo}>
    {/*    <div className={styles.layerPanelLogoIcon}><FaTreeCity /></div> */}
    {/* <div className={styles.layerPanelLogoText}>MightyAI Stems • Hackensack, NJ • Supported by Roots & Shoots Grant</div> */}
 
          <div className={styles.layerPanelLogoText}>
            <div style={{ fontSize: '22px', fontWeight: 'bold' }}>MightyAI Stems</div>
            <div style={{ fontSize: '16px' }}>Hackensack, NJ</div>
            <div style={{ fontSize: '14px' }}>•</div>
            <div style={{ fontSize: '14px' }}>Supported By</div>
            <div style={{ fontSize: '14px' }}>Jane Goodall Institute</div>
            <div style={{ fontSize: '14px' }}>Roots & Shoots USA</div>
           </div>
        </div>
      </div>
      
 {/*   <div className={styles.layerPanelTitle}>Data Layers</div>  */}
      
      <div className={styles.searchContainer}>
        <SearchBar
          onSearch={handleSearch}
          onAddressSelect={onAddressSelect}
          onClear={onAddressSelect ? () => onAddressSelect(null) : undefined}
          placeholder="Search US address (Census Geocoder)..."
        />
      </div>
      
      <div className={styles.layerToggles}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={sortedLayers.map(layer => layer.id)} strategy={verticalListSortingStrategy}>
            {sortedLayers.map((layer) => (
              <LayerToggle
                key={layer.id}
                layer={layer}
                isActive={layer.isActive}
                onToggle={onLayerToggle}
                onVisibilityToggle={onVisibilityToggle}
                onControlChange={handleControlChange}
              />
            ))}
          </SortableContext>
          <DragOverlay>
            {activeLayer ? (
              <div style={{ 
                background: 'rgba(255, 255, 255, 0.05)', 
                border: '1px solid rgba(255, 255, 255, 0.2)', 
                borderRadius: '12px', 
                padding: '15px',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)'
              }}>
                <div style={{ fontSize: '18px', width: '24px', textAlign: 'center' }}>
                  {activeLayer.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                    {activeLayer.name}
                  </div>
                  <div style={{ fontSize: '11px', opacity: 0.8 }}>
                    {activeLayer.description}
                  </div>
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
};

export default LayerPanel;
